<template>
  <main class="main main--has-floating-buttons">
    <template v-if="roomType !== null">
      <portal to="headerTitle">
        {{ roomType.name[hotelSettings.details.primaryLanguage] }}
      </portal>
      <tabable-card cols="12 6@desktop">
        <tabable-card-content :title="$t(`lang.${hotelSettings.details.primaryLanguage}`)">
          <hm-card-inner>
            <hm-input v-model="roomType.name[hotelSettings.details.primaryLanguage]"
                      :lazy="true"
                      :disabled="roomType.deleted"
                      :label="$t('admin.pageRoomType.title')"
                      validate="required"
                      name="title"
                      cols="12" />
            <hm-textarea v-model="roomType.description[hotelSettings.details.primaryLanguage]"
                         :lazy="true"
                         :disabled="roomType.deleted"
                         :label="$t('admin.pageRoomType.description')"
                         name="description"
                         cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>
      <tabable-card cols="12 6@desktop">
        <tabable-card-content v-for="lang in hotelSettings.details.additionalLanguages" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner>
            <hm-input v-model="roomType.name[lang]"
                      :lazy="true"
                      :disabled="roomType.deleted"
                      :label="$t('admin.pageRoomType.title')"
                      name="title"
                      cols="12" />
            <hm-textarea v-model="roomType.description[lang]"
                         :lazy="true"
                         :disabled="roomType.deleted"
                         :label="$t('admin.pageRoomType.description')"
                         name="description"
                         cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>
      <card :title="$t('admin.pageRoomType.card.occupancy')" cols="12 6@desktop">
        <hm-input v-model="roomType.id"
                  :disabled="true"
                  :label="$t('admin.pageRoomType.id')"
                  name="id"
                  cols="12" />
        <hm-input-select v-if="!hasChildren && (hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') || !isEmpty(roomType.parentRoomTypeId))"
                         v-model="roomType.parentRoomTypeId"
                         :disabled="(roomType.deleted) || (!isEmpty(roomType.parentRoomTypeId) && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                         :label="$t('admin.pageRoomType.parentRoomTypeId')"
                         name="parentRoomTypeId"
                         cols="12 6@tablet">
          <option :value="null"></option>
          <option v-for="item in hotelSettings.roomTypes.all(roomType.parentRoomTypeId)"
                  v-if="item.id !== roomType.id && isEmpty(item.parentRoomTypeId)"
                  :value="item.id"
                  :disabled="item.deleted"
                  v-html="item.name[$shortLocale]"></option>
        </hm-input-select>
        <hm-input-toggle v-model="roomType.occupancyBasedRates"
                         :disabled="roomType.deleted || !isEmpty(roomType.parentRoomTypeId)"
                         :label="$t('admin.pageRoomType.occupancyBasedRates')"
                         :enabled-title="$t('admin.pageRoomType.occupancyBasedRatesEnabled')"
                         :disabled-title="$t('admin.pageRoomType.occupancyBasedRatesDisabled')"
                         cols="12 6@tablet start-1" />
        <hm-col cols="12">
          <span class="info-text col-12">{{ $t('admin.pageRoomType.infoTextChangeOccupancy') }}</span>
        </hm-col>
        <hm-input-number v-model="roomType.minOccupancy"
                         :disabled="roomType.deleted || !isEmpty(roomType.parentRoomTypeId) || (roomType.google && !hasRole('ROLE_SYSADMIN'))"
                         :label="$t('admin.pageRoomType.minOccupancy')"
                         name="minOccupancy"
                         validate="required"
                         cols="12 4@tablet start-1" />
        <hm-input-number v-model="roomType.expectedOccupancy"
                         :disabled="roomType.deleted || !isEmpty(roomType.parentRoomTypeId) || (roomType.google && !hasRole('ROLE_SYSADMIN'))"
                         :label="$t('admin.pageRoomType.expectedOccupancy')"
                         name="expectedOccupancy"
                         validate="required"
                         cols="12 4@tablet" />
        <hm-input-number v-model="roomType.maxOccupancy"
                         :disabled="roomType.deleted || !isEmpty(roomType.parentRoomTypeId) || (roomType.google && !hasRole('ROLE_SYSADMIN'))"
                         :label="$t('admin.pageRoomType.maxOccupancy')"
                         name="maxOccupancy"
                         validate="required"
                         cols="12 4@tablet" />
        <hm-input v-model="roomType.iCalLink"
                  :disabled="true"
                  :label="$t('admin.pageRoomType.iCalLink')"
                  :help="$t('admin.pageRoomType.iCalLink_help')"
                  name="iCalLink"
                  cols="12">
          <template slot="icon-inline-right">
            <input-icon-button :border="true" :title="$t('admin.pageRoomType.copyToClipboard')" icon="copy" @click="$copy(roomType.iCalLink)" />
          </template>
        </hm-input>
      </card>
      <card :title="$t('admin.pageRoomType.card.general')" cols="12 6@desktop">
        <hm-input v-model="roomType.abbreviation"
                  :disabled="roomType.deleted"
                  :label="$t('admin.pageRoomType.abbreviation')"
                  :help="$t('admin.pageRoomType.abbreviation_help')"
                  name="abbreviation"
                  validate="required"
                  cols="12 6@tablet start-1" />
        <hm-input-number v-model="roomType.roomCount"
                         :disabled="true"
                         :label="$t('admin.pageRoomType.roomCount')"
                         name="roomCount"
                         cols="12 6@tablet" />
        <hm-roomtype-color-picker v-model="roomType.colorClass"
                                  :disabled="roomType.deleted"
                                  :label="$t('admin.pageRoomType.colorClass')"
                                  :help="$t('admin.pageRoomType.colorClass_help')"
                                  name="colorClass"
                                  cols="12 8@desktop start-1" />
        <hm-input-toggle v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" v-model="roomType.google" cols="12 6@tablet" label="Google?"/>

        <hm-input-select v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && hasLicense('ManageInventoryAsUnits')"
                         v-model="roomType.type"
                         :label="$t('pageRoomType.unitTypeType')"
                         name="unitTypeType"
                         enum-name="UnitTypeType"
                         cols="12 6@tablet start-1">
      </hm-input-select></card>
      <card v-if="hasAnyLicense(['OnlineBooking', 'InterfaceCmConnectS'])" :title="$t('pageRoomType.card.channelSpecificSettings')" cols="12">
        <hm-card-inner :title="$t('pageRoomType.card.onlineBooking')">
          <template v-if="hotelSettings.details.google">
            <hm-col cols="12">
              <span class="info-text col-12">{{ $t('admin.pageRoomType.infoTextGoogle') }}</span>
            </hm-col>
          </template>
          <hm-input-toggle v-model="roomType.publicVisible"
                           :disabled="roomType.deleted || (!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && hotelSettings.details.google && roomType.google && roomType.publicVisible)"
                           :label="$t('admin.pageRoomType.publicVisible')"
                           cols="12 6@tablet 4@desktop start-1" />
        </hm-card-inner>
        <hm-card-inner v-if="hasLicense('InterfaceCmConnectS')" title="Connect.S">
          <hm-input v-model="roomType.additionalFields.CM_SU_ID"
                    :disabled="true"
                    :label="$t('pageRoomType.connectSid')"
                    name="suRoomTypeId"
                    cols="12 6@tablet 4@desktop" />
          <hm-input-money v-model="roomType.additionalFields.CM_SU_RATE"
                          :currency="hotelSettings.details.currency"
                          :label="$t('pageRoomType.connectSdefaultRate')"
                          name="suRoomTypeRate"
                          cols="12 6@tablet 4@desktop" />
          <hm-input-select v-model="roomType.additionalFields.CM_SU_TYPE"
                           :label="$t('pageRoomType.connectSroomType')"
                           name="suRoomTypeType"
                           enum-name="SuRoomTypeType"
                           cols="12 6@tablet 4@desktop">
          </hm-input-select>
        </hm-card-inner>
        <hm-card-inner v-if="hasLicense('InterfaceCmConnectCX')" title="Connect.CX">
          <hm-input-select v-model="roomType.additionalFields.CM_CX_TYPE"
                           :label="$t('pageRoomType.connectCXroomType')"
                           name="cxRoomTypeType"
                           enum-name="ChannexRoomTypeType"
                           cols="12 6@tablet 4@desktop">
          </hm-input-select>
        </hm-card-inner>
      </card>
      <card v-if="!roomType.deleted || !isEmpty(roomType.imageIds)" :title="$t('admin.pageRoomType.card.images')" cols="12 6@desktop">
        <multi-image-uploader v-model="roomType.imageIds" :parent-id="roomTypeId" :disabled="roomType.deleted" :label="$t('admin.pageRoomType.imageIds')" />
      </card>
      <card v-if="!roomType.deleted || !isEmpty(roomType.layoutImageIds)" :title="$t('admin.pageRoomType.card.layoutImages')" cols="12 6@desktop">
        <multi-image-uploader v-model="roomType.layoutImageIds"
                              :parent-id="`layout-${roomTypeId}`"
                              :pad="true"
                              :disabled="roomType.deleted"
                              :label="$t('admin.pageRoomType.layoutImageIds')" />
      </card>
      <card v-if="!isNil(roomType.mandatoryInHouseServices) && roomType.mandatoryInHouseServices.length > 0" :title="$t('admin.pageRoomType.card.mandatoryAdditionalServices')" cols="12">
        <hm-card-inner v-for="(ihs, idx) in roomType.mandatoryInHouseServices"
                       :key="idx"
                       :deletable="!roomType.google || hasRole('ROLE_SYSADMIN')"
                       :title="$t('admin.pageRoomType.card.additionalService', { idx: idx+1 })"
                       @delete="deleteMandatoryIhs(ihs)">
          <hm-input-select v-model="ihs.inHouseServiceId"
                           :disabled="roomType.deleted"
                           :label="$t('admin.pageRoomType.inHouseServiceId')"
                           name="inHouseServiceId"
                           cols="12 4@tablet 3@desktop">
            <optgroup v-for="group in additionalServices" :label="group.group">
              <option v-for="item in group.options"
                      v-if="!isNil(item.amount)"
                      :value="item.id"
                      :disabled="item.disabled"
                      v-html="item.name"></option>
            </optgroup>
          </hm-input-select>
          <template v-if="!isEmpty(ihs) && !isEmpty(ihs.inHouseServiceId)">
            <hm-input-number v-model="ihs.count"
                             :disabled="roomType.deleted || (roomType.google && !hasRole('ROLE_SYSADMIN'))"
                             :label="$t('admin.pageRoomType.count')"
                             name="count"
                             validate="required"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-money :value="isEmpty(ihs.inHouseServiceId) ? null : findHotelSettingById('inHouseServices', ihs.inHouseServiceId).amount"
                            :lazy="true"
                            :disabled="true"
                            :label="$t('admin.pageRoomType.amount')"
                            name="amount"
                            cols="12 4@tablet 3@desktop">
              <template slot="icon-inline-right">
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerRoom'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-room" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerPerson'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-person" />
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerStay'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-stay" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerNight'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-night" />
              </template>
            </hm-input-money>
            <hm-input-money v-model="ihs.amountOverride"
                            :lazy="true"
                            :disabled="roomType.deleted"
                            :label="$t('admin.pageRoomType.amountOverride')"
                            name="amountOverride"
                            cols="12 4@tablet 3@desktop" />
            <!--
            <hm-input-toggle v-model="ihs.applyRatePlanTaxes" label="Verwende Tarif-Steuern?" cols="12 4@tablet" help="Sollen die Tarif-Steuern, statt der Steuern auf der Zusatzleistung verwendet werden." />
            -->
            <hm-input-toggle v-model="ihs.includeForPrepaymentCalculation" :disabled="roomType.deleted" :label="$t('admin.pageRoomType.includeForPrepaymentCalculation')" cols="12 4@tablet 3@desktop" />
          </template>
        </hm-card-inner>
      </card>


      <card v-if="!isNil(roomType.bookableInHouseServices) && roomType.bookableInHouseServices.length > 0" :title="$t('admin.pageRoomType.card.bookableAdditionalServices')" cols="12">
        <hm-card-inner v-for="(ihs, idx) in roomType.bookableInHouseServices"
                       :key="idx"
                       :deletable="true"
                       :title="$t('admin.pageRoomType.card.bookableAdditionalService', { idx: idx+1 })"
                       @delete="deleteBookableIhs(ihs)">
          <hm-input-select v-model="ihs.inHouseServiceId"
                           :disabled="roomType.deleted"
                           :label="$t('admin.pageRoomType.inHouseServiceId')"
                           name="inHouseServiceId"
                           cols="12 4@tablet 3@desktop">
            <optgroup v-for="group in additionalServices" :label="group.group">
              <option v-for="item in group.options"
                      v-if="!isNil(item.amount)"
                      :value="item.id"
                      :disabled="item.disabled"
                      v-html="item.name"></option>
            </optgroup>
          </hm-input-select>
          <template v-if="!isEmpty(ihs) && !isEmpty(ihs.inHouseServiceId)">
            <hm-input-number v-model="ihs.minCount"
                             :disabled="roomType.deleted"
                             :label="$t('admin.pageRoomType.minCount')"
                             :validate="`required|max_value:100`"
                             name="minCount"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-number v-model="ihs.maxCount"
                             :disabled="roomType.deleted"
                             :label="$t('admin.pageRoomType.maxCount')"
                             :validate="`required|max_value:100`"
                             name="maxCount"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-money :value="isEmpty(ihs.inHouseServiceId) ? null : findHotelSettingById('inHouseServices', ihs.inHouseServiceId).amount"
                            :lazy="true"
                            :disabled="true"
                            :label="$t('admin.pageRoomType.amount')"
                            name="amount"
                            cols="12 4@tablet 3@desktop">
              <template slot="icon-inline-right">
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerRoom'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-room" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerPerson'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-person" />
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerStay'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-stay" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerNight'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-night" />
              </template>
            </hm-input-money>
            <hm-input-toggle v-model="ihs.includeForPrepaymentCalculation" :disabled="roomType.deleted" :label="$t('admin.pageRoomType.includeForPrepaymentCalculation')" cols="12 4@tablet 3@desktop" />
          </template>
        </hm-card-inner>
      </card>

      <ai-text-generator />

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!roomType.deleted && roomType.roomCount === 0"
                            :label="$t('admin.pageRoomType.actions.deleteRoomType')"
                            icon="delete"
                            color="error"
                            @click="deleteRoomType(false)" />
        <hm-floating-button v-if="!roomType.deleted"
                            label="AI Text"
                            icon="magic-wand"
                            color="blue"
                            @click="clickGenerateDescription"/>
        <hm-floating-button v-if="!roomType.deleted && hasLicense('AdditionalServices')" :label="$t('admin.pageRoomType.actions.more')" icon="plus" color="blue">
          <hm-floating-child-button v-if="hasLicense('OnlineBookingAdditionalServices')"
                                    :label="$t('admin.pageRoomType.actions.addBookableAdditionalService')"
                                    icon="plus"
                                    color="blue"
                                    @click="addBookableIhs()" />
          <hm-floating-child-button v-if="hasLicense('AdditionalServices')"
                                    :label="$t('admin.pageRoomType.actions.addMandatoryAdditionalService')"
                                    :disabled="roomType.google && !hasRole('ROLE_SYSADMIN')"
                                    icon="plus"
                                    color="blue"
                                    @click="addMandatoryIhs()" />
        </hm-floating-button>
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import AiTextGenerator from '../../lib/util/AiTextGenerator';
import HmInputToggle from '../../lib/inputs/HmInputToggle';
import HmCol from '../../lib/layout/HmCol.vue';

export default {
  name: 'PageRoomType',
  components: { HmCol, HmInputToggle, AiTextGenerator, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'roomType',
      variable: 'roomType',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.roomType.deleted === true) return;

        const toSave = cloneDeep(this.roomType);

        toSave.mandatoryInHouseServices = toSave.mandatoryInHouseServices.filter(it => !isEmpty(it.inHouseServiceId));
        toSave.bookableInHouseServices = isNil(toSave.bookableInHouseServices) ? [] : toSave.bookableInHouseServices.filter(it => !isEmpty(it.inHouseServiceId));

        this.axios.put(this.serviceUrls.roomType.get(this, this.roomTypeId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      roomType: null,
      roomTypeId: this.$route.params.roomTypeId,
      aiKeywords: null,
      aiDescription: null,
    };
  },

  computed: {
    hasChildren: function () {
      return this.hotelSettings.roomTypes.all()
        .filter(it => !it.deleted)
        .filter(it => it.parentRoomTypeId === this.roomType.id)
        .length > 0;
    },
    additionalServices: function () {
      return [
        ...sortBy(
          Object.entries(groupBy(this.hotelSettings.inHouseServices.all().filter(it => it.level === 'RoomType'), it => it.type))
            .map(it => ({
              group: this.$t(`InHouseServiceType.${it[0]}`),
              options: sortBy(it[1].map((ihs) => {
                const ret = {
                  id: ihs.id,
                  name: ihs.name[this.$shortLocale],
                  type: 'InHouseService',
                  amount: ihs.amount,
                  disabled: this.includedMandatoryIhsIds.includes(ihs.id) || ihs.deleted,
                };

                if (ihs.rateFrequency === 'PerNight') {
                  ret.name = `${ret.name} (${this.$t('RateFrequency.PerNight')})`;
                }

                return ret;
              }), ihs => ihs.name),
            })),
          it => it.group,
        ),
      ];
    },
    includedMandatoryIhsIds: function () {
      return isNil(this.roomType.mandatoryInHouseServices) ? [] : this.roomType.mandatoryInHouseServices.map(it => it.inHouseServiceId);
    },
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    isEmpty,
    loadData() {
      this.axios.get(this.serviceUrls.roomType.get(this, this.roomTypeId))
        .then((response) => {
          if (isNil(this.roomType)) {
            const tmp = response.data.data;
            if (tmp.additionalFields) {
              if (!isEmpty(tmp.additionalFields.CM_SU_RATE)) {
                try {
                  tmp.additionalFields.CM_SU_RATE = parseFloat(tmp.additionalFields.CM_SU_RATE);
                } catch (e) { /* ignore */ }
              }
            }
            this.roomType = tmp;
          }
        });
    },
    deleteBookableIhs(ihs) {
      const idx = this.roomType.bookableInHouseServices.indexOf(ihs);
      if (idx >= 0) {
        this.roomType.bookableInHouseServices.splice(idx, 1);
      }
    },
    deleteMandatoryIhs(ihs) {
      const idx = this.roomType.mandatoryInHouseServices.indexOf(ihs);
      if (idx >= 0) {
        this.roomType.mandatoryInHouseServices.splice(idx, 1);
      }
    },
    addMandatoryIhs() {
      this.roomType.mandatoryInHouseServices.push({
        count: 0,
        applyRatePlanTaxes: false,
        includeForPrepaymentCalculation: false,
        amountOverride: null,
        inHouseServiceId: null,
        hotelId: this.hotelSettings.hotel.id,
      });
    },
    addBookableIhs() {
      this.roomType.bookableInHouseServices.push({
        minCount: 1,
        maxCount: 1,
        includeForPrepaymentCalculation: false,
        inHouseServiceId: null,
        hotelId: this.hotelSettings.hotel.id,
      });
    },
    deleteRoomType(confirmed = false) {
      if (this.roomType.roomCount === 0) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.roomType.get(this, this.roomTypeId))
            .then((response) => {
              this.roomType = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('roomType.list');
              this.$router.push({
                name: 'roomTypes',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('admin.pageRoomType.confirmDeleteRoomType'),
            (doNotShowAgain) => {
              this.deleteRoomType(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pageRoomType.needToDeleteAllRoomsOfThisTypeFirst'));
      }
    },
    clickGenerateDescription() {
      this.$serviceBus.$emit('aiTextGenerator.show', this.serviceUrls.aiTextGenerator.roomTypeDescription(this, this.roomTypeId));
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageRoomType.vue